import { useEffect, useRef, useState } from 'react';

function About() {
  // État pour les compteurs animés
  const [counts, setCounts] = useState([0, 0, 0]);
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  const stats = [
    { value: 500, suffix: '+', label: 'Formations dispensées' },
    { value: 200, suffix: '+', label: 'Professionnels formés' },
    { value: 95, suffix: '%', label: 'Satisfaction client' },
  ];

  // Animation des compteurs au scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [hasAnimated]);

  useEffect(() => {
    if (!hasAnimated) return;
    const duration = 2000;
    const startTime = performance.now();

    const animate = (time) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const newCounts = stats.map((s) => Math.floor(s.value * eased));
      setCounts(newCounts);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [hasAnimated]);

  return (
    <>
      <style>{`
        /* ============================================
           ABOUT – fond bleu solide
           ============================================ */
        .ve-about-section {
          padding: 100px 0 80px;
          background: #0a1628;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #ffffff;
          position: relative;
          overflow: hidden;
            max-width: 100%;
        }

        .ve-about-section::before {
          content: '';
          position: absolute;
          top: -30%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(42, 95, 168, 0.10), transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          position: relative;
          z-index: 2;
        }

        /* En-tête */
        .ve-about-header {
          text-align: center;
          margin-bottom: 56px;
          animation: fadeUp 0.8s ease forwards;
          opacity: 0;
          transform: translateY(30px);
        }

        .ve-section-tag {
          display: inline-block;
          background: rgba(74, 144, 226, 0.15);
          color: #4a90e2;
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 0.5px;
          padding: 6px 20px;
          border-radius: 50px;
          text-transform: uppercase;
          margin-bottom: 16px;
        }

        .ve-about-header h2 {
          font-size: clamp(2rem, 4.5vw, 3.2rem);
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 12px;
          letter-spacing: -0.5px;
        }

        .ve-about-header h2 span {
          background: linear-gradient(135deg, #4a90e2, #6bb3e8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .ve-about-header p {
          font-size: 1.1rem;
          color: rgba(255,255,255,0.75);
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.7;
        }

        /* Contenu principal (image + texte) */
        .ve-about-content {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 50px;
          margin-bottom: 70px;
        }

        .ve-about-image {
          flex: 1 1 45%;
          min-width: 280px;
          border-radius: 28px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.4);
          aspect-ratio: 4/3;
          background-size: cover;
          background-position: center;
          background-color: #1a3a6b;
          transition: transform 0.6s ease;
        }

        .ve-about-image:hover {
          transform: scale(1.02);
        }

        .ve-about-text {
          flex: 1 1 45%;
          min-width: 280px;
          animation: fadeUp 0.8s ease 0.2s forwards;
          opacity: 0;
          transform: translateY(30px);
        }

        .ve-about-text h3 {
          font-size: 2rem;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 16px;
        }

        .ve-about-text h3 span {
          color: #4a90e2;
        }

        .ve-about-text p {
          font-size: 1.05rem;
          line-height: 1.7;
          color: rgba(255,255,255,0.75);
          margin: 0 0 24px;
        }

        .ve-about-text .ve-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 36px;
          background: linear-gradient(135deg, #2b7fd4, #4a90e2);
          color: #ffffff;
          font-weight: 600;
          font-size: 1rem;
          border-radius: 50px;
          text-decoration: none;
          box-shadow: 0 0 28px rgba(42, 95, 168, 0.35);
          transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
          border: none;
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .ve-about-text .ve-btn-primary::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #4a90e2, #2b7fd4);
          opacity: 0;
          transition: opacity 0.4s ease;
          border-radius: 50px;
        }

        .ve-about-text .ve-btn-primary:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 0 48px rgba(42, 95, 168, 0.55);
        }

        .ve-about-text .ve-btn-primary:hover::before {
          opacity: 1;
        }

        .ve-about-text .ve-btn-primary span {
          position: relative;
          z-index: 1;
        }

        /* Cartes valeurs */
        .ve-values-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 30px;
          margin-bottom: 60px;
        }

        .ve-value-card {
          background: rgba(255,255,255,0.05);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 30px 20px;
          text-align: center;
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.3s;
          animation: fadeUp 0.8s ease forwards;
          opacity: 0;
          transform: translateY(30px);
        }

        .ve-value-card:nth-child(1) { animation-delay: 0.05s; }
        .ve-value-card:nth-child(2) { animation-delay: 0.10s; }
        .ve-value-card:nth-child(3) { animation-delay: 0.15s; }

        .ve-value-card:hover {
          transform: translateY(-8px) scale(1.02);
          background: rgba(255,255,255,0.08);
          border-color: rgba(74, 144, 226, 0.3);
        }

        .ve-value-card i {
          font-size: 2.8rem;
          color: #4a90e2;
          margin-bottom: 16px;
          transition: transform 0.4s ease;
        }

        .ve-value-card:hover i {
          transform: scale(1.1) rotate(-4deg);
        }

        .ve-value-card h4 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 8px;
        }

        .ve-value-card p {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.7);
          line-height: 1.5;
          margin: 0;
        }

        /* Statistiques */
        .ve-about-stats {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 40px 60px;
          padding: 30px 0 10px;
          border-top: 1px solid rgba(255,255,255,0.06);
        }

        .ve-stat-item {
          text-align: center;
          min-width: 140px;
        }

        .ve-stat-item strong {
          display: block;
          font-size: 3rem;
          font-weight: 800;
          background: linear-gradient(135deg, #ffffff, #b0c4de);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: -1px;
        }

        .ve-stat-item strong span {
          font-size: 2rem;
          -webkit-text-fill-color: #4a90e2;
          margin-left: 2px;
        }

        .ve-stat-item p {
          font-size: 1rem;
          color: rgba(255,255,255,0.7);
          margin: 4px 0 0;
          font-weight: 500;
        }

        /* Animations */
        @keyframes fadeUp {
          to { opacity: 1; transform: translateY(0); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .ve-about-section {
            padding: 70px 0 50px;
          }
          .ve-about-content {
            flex-direction: column;
          }
          .ve-about-image {
            width: 100%;
            aspect-ratio: 4/3;
          }
          .ve-about-text {
            text-align: center;
          }
          .ve-about-text .ve-btn-primary {
            margin: 0 auto;
          }
          .ve-values-grid {
            grid-template-columns: 1fr 1fr;
          }
          .ve-about-stats {
            gap: 30px;
          }
          .ve-stat-item strong {
            font-size: 2.4rem;
          }
        }

        @media (max-width: 480px) {
          .ve-values-grid {
            grid-template-columns: 1fr;
            max-width: 320px;
            margin-left: auto;
            margin-right: auto;
          }
          .ve-about-stats {
            flex-direction: column;
            gap: 20px;
            align-items: center;
          }
          .ve-about-text h3 {
            font-size: 1.6rem;
          }
          .ve-stat-item strong {
            font-size: 2rem;
          }
        }

        /* Fallback FontAwesome */
        .fa, .fas, .far, .fal, .fab {
          font-family: 'Font Awesome 6 Free' !important;
          font-weight: 900;
        }
        .fa-bullseye::before { content: "🎯"; font-style: normal; }
        .fa-handshake-o::before { content: "🤝"; font-style: normal; }
        .fa-rocket::before { content: "🚀"; font-style: normal; }
        .fa-arrow-right::before { content: "→"; font-style: normal; }
      `}</style>

      <section className="ve-about-section" ref={sectionRef}>
        <div className="container">
          {/* En-tête */}
          <div className="ve-about-header">
            <span className="ve-section-tag">About Us</span>
            <h2>Pourquoi <span>3iSENTER</span> ?</h2>
            <p>
              Nous proposons des formations pratiques et <strong>certifiées</strong> adaptées aux besoins des clients et entreprises.
              Notre expertise couvre la gestion des ressources humaines, le recrutement et le placement de talents.
            </p>
          </div>

          {/* Contenu image + texte */}
          <div className="ve-about-content">
            <div
              className="ve-about-image"
              style={{ backgroundImage: 'url(/img/bi1.png)' }}
            ></div>
            <div className="ve-about-text">
              <h3>Une approche <span>humaine</span> et <span>innovante</span></h3>
              <p>
                Chez 3iSENTER, nous croyons que la formation est la clé de la réussite professionnelle.
                Nous mettons un point d'honneur à offrir des programmes sur mesure, animés par des experts
                passionnés, pour transformer vos compétences en atouts concrets.
              </p>
              <a href="/contact" className="ve-btn-primary"><span>Nous contacter</span></a>
            </div>
          </div>

          {/* Cartes valeurs */}
          <div className="ve-values-grid">
            <div className="ve-value-card">
              <i className="fa fa-bullseye"></i>
              <h4>Objectif clair</h4>
              <p>Des formations alignées avec vos objectifs professionnels pour un impact immédiat.</p>
            </div>
            <div className="ve-value-card">
              <i className="fa fa-handshake-o"></i>
              <h4>Accompagnement personnalisé</h4>
              <p>Un suivi individualisé pour chaque apprenant, de l'inscription à la certification.</p>
            </div>
            <div className="ve-value-card">
              <i className="fa fa-rocket"></i>
              <h4>Innovation pédagogique</h4>
              <p>Des méthodes modernes et interactives pour une montée en compétences rapide.</p>
            </div>
          </div>

          {/* Statistiques animées */}
          <div className="ve-about-stats">
            {stats.map((stat, idx) => (
              <div className="ve-stat-item" key={idx}>
                <strong>
                  {counts[idx] ?? 0}
                  <span>{stat.suffix}</span>
                </strong>
                <p>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default About;